import { useCallback, useEffect, useState } from 'react';
import { isDevEnv } from '../environment';
import { useSelector } from 'react-redux';
import { selectUserInfoData } from '../reducers/userInfoReducer';
import { useEnsureLdClient } from './useEnsureLdClient';
import type { LDFlagSet } from 'launchdarkly-js-sdk-common';

export const useInitializeLd = () => {
  const ldClient = useEnsureLdClient();
  const userInfo = useSelector(selectUserInfoData);

  const { id, name, email } = userInfo ?? {};

  const initializeLD = useCallback((): Promise<LDFlagSet | null> => {
    return new Promise((resolve, reject) => {
      const onDone = (err: Error | null, flags: LDFlagSet | null) => {
        if (err) {
          reject(err);
        } else {
          resolve(flags);
        }
      };
      if (id) {
        const nameOrEmail = name || email || 'Unknown';
        const _name = isDevEnv ? nameOrEmail + ' (DEV)' : nameOrEmail;

        /**
         * Important!
         * Exclude auth0 generated users from LD experiments to keep the quota
         * For each run of e2e tests, we will use the same user for LD.
         */
        const sweepEmailAutomationSuffix = '@sweepteam.testinator.com';
        if (email?.endsWith(sweepEmailAutomationSuffix)) {
          ldClient.identify(
            { key: 'sweep_automation_user', name: 'Sweep Automation User' },
            undefined,
            onDone,
          );
        } else {
          ldClient.identify({ key: id, email, name: _name }, undefined, onDone);
        }
      }
    });
  }, [email, id, ldClient, name]);

  return {
    initializeLD,
  };
};

export const useInitializeLdEffect = () => {
  const [ldReady, setLdReady] = useState(false);

  const { initializeLD } = useInitializeLd();

  useEffect(() => {
    // We don't want to block the main thread if launchdarkly fails to initialize
    // so we use .finally() instead of .then()

    initializeLD().finally(() => {
      setLdReady(true);
    });
  }, [initializeLD]);

  return {
    ldReady,
  };
};
